/**
 * Wallet inspector — the screen for job 2, "the merchant says they were paid".
 *
 * The statement is in TENANT terms: money in is positive, money out is negative. That is what the merchant
 * on the phone is looking at, and matching their view is the whole point. Internally the ledger uses
 * accounting signs; the conversion happens once, at the engine's edge.
 *
 * A balance is never shown without its as-of time.
 */
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLoader } from "../lib/session";
import { dateTimeLong, money, relative } from "../lib/format";
import { C, card, CardHead, EmptyState, ErrorState, Skeleton, Warn } from "../ui/primitives";

export function Wallets() {
  const { tenantId } = useParams();
  return tenantId ? <OneWallet tenantId={tenantId} /> : <WalletList />;
}

function WalletList() {
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");
  const { data, error, loading, reload } = useLoader(() => api.wallets({ q: applied || undefined }), [applied]);

  return (
    <div style={card}>
      <CardHead title="Tenant wallets" sub="Every wallet the engine holds money in. A wallet exists from the moment its tenant is onboarded, whether or not money has moved yet."
        right={
          <div style={{ display: "flex", gap: 7, alignItems: "center", padding: "8px 11px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--subtle)", minWidth: 220 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2.2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setApplied(q.trim()); }}
              placeholder="Find a wallet by tenant id…" style={{ border: "none", background: "none", color: "var(--ink)", fontSize: 12, fontWeight: 600, flex: 1, minWidth: 0 }} />
          </div>
        } />
      {loading ? <Skeleton label="Loading wallets…" />
        : error ? <ErrorState title="Couldn't load wallets." body={error} onRetry={reload} />
        : !data || data.wallets.length === 0 ? (
          <EmptyState title="No wallets match." body="No tenant has been onboarded under that id yet. A wallet appears here the moment its tenant's merchant profile is created." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {data.wallets.map((w) => (
              <Link key={w.tenantId} to={`/wallets/${encodeURIComponent(w.tenantId)}`}
                style={{ display: "grid", gridTemplateColumns: "1fr 130px 160px", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--line)", alignItems: "center", color: "var(--ink)" }}>
                <span style={{ minWidth: 0, overflow: "hidden" }}>
                  {/* Name is a LABEL; the id is the identity and stays visible so a stale name can never
                      send an operator to the wrong tenant's money. */}
                  <span style={{ fontSize: 12.5, fontWeight: 700, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {w.displayName ?? <span style={{ color: "var(--muted)", fontWeight: 600 }}>Unnamed tenant</span>}
                  </span>
                  <span className="bp-mono" style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 600 }}>{w.tenantId}</span>
                </span>
                <span className="bp-mono" style={{ fontSize: 13, fontWeight: 700, textAlign: "right", color: w.balance < 0 ? C.danger : "var(--ink)" }}>{money(w.balance)}</span>
                <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, textAlign: "right" }}>
                  {w.allowNegative ? "overdraft enabled · " : ""}{relative(w.updatedAt)}
                </span>
              </Link>
            ))}
          </div>
        )}
    </div>
  );
}

function OneWallet({ tenantId }: { tenantId: string }) {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<string | undefined>();
  const [older, setOlder] = useState<NonNullable<Awaited<ReturnType<typeof api.wallet>>>["statement"]>([]);
  const { data, error, loading, reload } = useLoader(() => api.wallet(tenantId, cursor), [tenantId, cursor]);

  const asOf = dateTimeLong(new Date(), { tz: true });
  const lines = [...older, ...(data?.statement ?? [])];

  // A running balance read downward from the current one, so the operator can follow the arithmetic the
  // same way the merchant does.
  let running = data?.balance ?? 0;
  const withRunning = lines.map((l) => { const at = running; running = Math.round((running - l.amount) * 100) / 100; return { ...l, running: at }; });

  if (loading && lines.length === 0) return <div style={card}><Skeleton label="Loading wallet…" /></div>;
  if (error || !data) return <div style={card}><ErrorState title="Couldn't load this wallet." body={error ?? "The engine did not answer."} onRetry={reload} /></div>;

  return (
    <>
      <button type="button" onClick={() => navigate("/wallets")} style={{ alignSelf: "flex-start", border: "none", background: "none", color: C.accent, fontSize: 12, fontWeight: 800, cursor: "pointer", padding: 0 }}>← All wallets</button>

      <div style={card}>
        <div style={{ padding: "15px 17px", borderBottom: "1px solid var(--line)", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--accentsoft)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: C.accent, flex: "none" }}>
            {tenantId.slice(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>
              {data.displayName ?? <span style={{ color: "var(--muted)" }}>Unnamed tenant</span>}
            </div>
            <div className="bp-mono" style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>
              {tenantId} · {data.currency}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="bp-mono" style={{ fontSize: 20, fontWeight: 700, color: data.balance < 0 ? C.danger : "var(--ink)" }}>{money(data.balance)}</div>
            <div style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 700 }}>balance as of {asOf}</div>
          </div>
        </div>

        {data.balance < 0 && (
          <div style={{ padding: "12px 17px", borderBottom: `1px solid ${C.dangerLine}`, background: C.dangerSoft, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Warn size={16} />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink2)" }}>
              This wallet is <b style={{ color: C.danger }}>negative: {money(data.balance)}</b> in tenant terms.{" "}
              <Link to="/flags">See the flag</Link>
            </span>
          </div>
        )}

        <div style={{ padding: "9px 17px", background: "var(--accentsoft)", borderBottom: "1px solid var(--line2)", fontSize: 11, fontWeight: 700, color: "var(--ink2)" }}>
          Statement in <b>tenant terms</b> — money in is positive, money out is negative. This is the view the merchant on the phone is looking at.
        </div>

        {withRunning.length === 0 ? (
          <EmptyState title="Nothing has moved for this tenant yet."
            body="A zero balance with zero movements is a true statement, not missing data — the first collection will appear here the moment it posts." />
        ) : (
          <>
            {withRunning.map((l) => (
              <div key={l.id} style={{ display: "grid", gridTemplateColumns: "170px 1fr 120px 130px", gap: 10, padding: "12px 17px", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{dateTimeLong(l.at)}</div>
                  <div style={{ fontSize: 10.5, color: "var(--muted)", fontWeight: 600 }}>{relative(l.at)}</div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>{describe(l.kind)}{l.memo ? ` — ${l.memo}` : ""}</div>
                  {l.refId && <Link to={`/movements?refId=${encodeURIComponent(l.refId)}`} className="bp-mono" style={{ fontSize: 11, fontWeight: 700 }}>{l.refId.slice(0, 12)} →</Link>}
                </div>
                <span className="bp-mono" style={{ fontSize: 13, fontWeight: 800, textAlign: "right", color: l.amount < 0 ? C.danger : C.good }}>{money(l.amount, { signed: true })}</span>
                <span className="bp-mono" style={{ fontSize: 12, fontWeight: 700, color: "var(--ink2)", textAlign: "right" }}>{money(l.running)}</span>
              </div>
            ))}
            {data.nextCursor && (
              <button type="button" onClick={() => { setOlder(lines); setCursor(data.nextCursor!); }}
                style={{ width: "100%", padding: 13, border: "none", background: "var(--subtle)", color: C.accent, fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                Load earlier statement lines
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}

function describe(kind: string): string {
  switch (kind) {
    case "collection_confirmed": return "Payment received";
    case "collection_fee": return "Fee";
    case "payout_initiated": return "Payout";
    case "payout_confirmed": return "Payout confirmed";
    case "payout_bounced": return "Payout returned by the bank";
    default: return kind.replace(/_/g, " ");
  }
}
